import {
  Component,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ServiceTemplateService } from '../../../../core/services/service-template.service';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import {
  IServiceTemplate,
  IServiceTemplateDetails,
} from '../../../../core/models/service-template/service-template.model';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IServiceConfigStep } from '../../../../core/models/service-change-request-config-steps-forms.model';

@Component({
  selector: 'irembogov-template-preview',
  templateUrl: './template-preview.component.html',
  styleUrls: ['./template-preview.component.scss'],
})
export class TemplatePreviewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  templateId!: string;
  template!: IServiceTemplate;
  isLoading = false;
  loadError = false;
  currentPreviewStep = 0;
  showFormSummary = false; // Controls form summary vs form preview
  showWorkflowRaw = false; // Controls workflow visual vs raw data

  @ViewChild('overview', { static: true }) overviewTemplate!: TemplateRef<any>;
  @ViewChild('journeymapping', { static: false })
  journeymappingTemplate!: TemplateRef<any>;
  @ViewChild('formbuilder', { static: false })
  formbuilderTemplate!: TemplateRef<any>;
  @ViewChild('pricing', { static: false }) pricingTemplate!: TemplateRef<any>;
  @ViewChild('workflow', { static: false }) workflowTemplate!: TemplateRef<any>;
  @ViewChild('officerform', { static: false })
  officerformTemplate!: TemplateRef<any>;
  @ViewChild('translation', { static: false })
  translationTemplate!: TemplateRef<any>;

  // Base steps - will be modified based on service type
  basePreviewSteps: IServiceConfigStep[] = [
    {
      label: 'Overview',
      key: 'overview',
      icon: 'fas fa-info-circle',
      isComplete: true, // Always complete since template exists
      hasError: false,
    },
    {
      label: 'Form Builder',
      key: 'formbuilder',
      icon: 'fas fa-border-none',
      isComplete: true, // Always enabled for navigation
      hasError: false,
    },
    {
      label: 'Pricing',
      key: 'pricing',
      icon: 'fas fa-money-bill-1-wave',
      isComplete: true, // Always enabled for navigation
      hasError: false,
    },
    {
      label: 'Workflow',
      key: 'workflow',
      icon: 'fas fa-diagram-project',
      isComplete: true, // Always enabled for navigation
      hasError: false,
    },
    {
      label: 'Officer Form',
      key: 'officerform',
      icon: 'fas fa-file',
      isComplete: true, // Always enabled for navigation
      hasError: false,
    },
    // {
    //   label: 'Translation',
    //   key: 'translation',
    //   icon: 'fas fa-language',
    //   isComplete: false,
    //   hasError: false,
    // },
  ];

  previewSteps: IServiceConfigStep[] = [];

  // Template data
  templateFormData!: Record<string, FormlyFieldConfig[]>;
  templatePricingData!: Record<string, unknown>;
  templateWorkflowData!: Record<string, unknown>;
  templateTranslationData!: Record<string, Record<string, unknown>>;
  templateThirdPartyFormData!: Record<string, Record<string, unknown>>;
  templateJourneyMappingData!: Record<string, unknown>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private serviceTemplateService: ServiceTemplateService,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private formBuilderService: FormBuilderService
  ) {}

  ngOnInit(): void {
    // Initialize FormBuilderService for form preview functionality
    this.formBuilderService.startFormBuilderConfigurationSubjects();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.templateId = params['id'];
      if (this.templateId) {
        this.loadTemplateData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Clean up FormBuilderService
    this.formBuilderService.completeFormBuilderConfigurationSubjects();
  }

  private loadTemplateData(): void {
    this.isLoading = true;
    this.loadError = false;
    this.serviceTemplateService
      .getTemplateById(this.templateId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: response => {
          this.template = response.data;
          this.updatePreviewSteps();
          this.loadTemplateDetails();
        },
        error: () => {
          this.loadError = true;
        },
      });
  }

  private updatePreviewSteps(): void {
    // Start with base steps
    this.previewSteps = [...this.basePreviewSteps];

    // Add journey mapping step if service type is JOURNEY
    if (this.template?.serviceType === 'JOURNEY') {
      const journeyMappingStep: IServiceConfigStep = {
        label: 'Journey Mapping',
        key: 'journeymapping',
        icon: 'fas fa-map',
        isComplete: true, // Always enabled for navigation
        hasError: false,
      };

      // Insert journey mapping after overview (index 1)
      this.previewSteps.splice(1, 0, journeyMappingStep);
    }
  }

  private loadTemplateDetails(): void {
    // Load all template configuration details
    this.loadTemplateFormDefinition();
    this.loadTemplatePricing();
    this.loadTemplateWorkflow();
    this.loadTemplateTranslation();
    this.loadTemplateThirdPartyForm();
    if (this.template?.serviceType === 'JOURNEY') {
      this.loadTemplateJourneyMapping();
    }
  }

  private loadTemplateFormDefinition(): void {
    this.serviceTemplateService
      .getTemplateFormDefinition(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.templateFormData = response.data || {};
          // Load form data for preview functionality - this is key!
          this.formBuilderService.loadJsonFormDataForPreview(
            this.templateFormData
          );
        },
        error: () => {
          this.templateFormData = {};
        },
      });
  }

  private loadTemplatePricing(): void {
    this.serviceTemplateService
      .getTemplatePricing(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.templatePricingData = response.data || {};
        },
        error: () => {
          this.templatePricingData = {};
        },
      });
  }

  private loadTemplateWorkflow(): void {
    this.serviceTemplateService
      .getTemplateWorkflowDefinition(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.templateWorkflowData = response.data || {};
        },
        error: () => {
          this.templateWorkflowData = {};
        },
      });
  }

  private loadTemplateTranslation(): void {
    this.serviceTemplateService
      .getTemplateTranslation(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.templateTranslationData = response.data || {};
        },
        error: () => {
          this.templateTranslationData = {};
        },
      });
  }

  private loadTemplateThirdPartyForm(): void {
    this.serviceTemplateService
      .getTemplateThirdPartyFormDefinition(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.templateThirdPartyFormData = response.data || {};
        },
        error: () => {
          this.templateThirdPartyFormData = {};
        },
      });
  }

  private loadTemplateJourneyMapping(): void {
    this.serviceTemplateService
      .getTemplateJourneyMapping(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.templateJourneyMappingData = response.data || {};
        },
        error: () => {
          this.templateJourneyMappingData = {};
        },
      });
  }

  getCurrentTemplate(index: number): TemplateRef<ElementRef> {
    if (index >= 0 && index < this.previewSteps.length) {
      const templateKey = `${this.previewSteps[index].key}Template`;
      return this[
        templateKey as keyof TemplatePreviewComponent
      ] as TemplateRef<ElementRef>;
    }
    return this.overviewTemplate;
  }

  onStepChange(event: any): void {
    // Handle both direct index and event object
    const index = typeof event === 'number' ? event : event.selectedIndex;
    this.currentPreviewStep = index;
  }

  toggleFormSummary(): void {
    this.showFormSummary = !this.showFormSummary;
  }

  toggleWorkflowView(): void {
    this.showWorkflowRaw = !this.showWorkflowRaw;
  }

  goBack(): void {
    this.router.navigate(['/services/templates']);
  }

  useTemplate(): void {
    // Navigate to create new change request with this template
    this.router.navigate(['/services/configure/new'], {
      queryParams: { templateId: this.templateId },
    });
  }

  // Helper methods for template data display

  getStepStatus(stepKey: string): boolean {
    // For form builder, check if there's actual form data
    if (stepKey === 'formbuilder') {
      return (
        this.templateFormData && Object.keys(this.templateFormData).length > 0
      );
    }
    // For pricing, check if there's actual pricing data
    if (stepKey === 'pricing') {
      return (
        this.templatePricingData &&
        Object.keys(this.templatePricingData).length > 0
      );
    }
    // For workflow, check if there's actual workflow data
    if (stepKey === 'workflow') {
      return (
        this.templateWorkflowData &&
        Object.keys(this.templateWorkflowData).length > 0
      );
    }
    // For officer form, check if there's actual form data
    if (stepKey === 'officerform') {
      return (
        this.templateThirdPartyFormData &&
        Object.keys(this.templateThirdPartyFormData).length > 0
      );
    }
    // For journey mapping, check if there's actual data
    if (stepKey === 'journeymapping') {
      return (
        this.templateJourneyMappingData &&
        Object.keys(this.templateJourneyMappingData).length > 0
      );
    }
    // For translation, check if there's actual data
    if (stepKey === 'translation') {
      return (
        this.templateTranslationData &&
        Object.keys(this.templateTranslationData).length > 0
      );
    }

    return true; // Default to true for navigation
  }

  getSectionCount(): number {
    return this.templateFormData
      ? Object.keys(this.templateFormData).length
      : 0;
  }

  getTotalFieldCount(): number {
    if (!this.templateFormData) return 0;
    return Object.values(this.templateFormData).reduce((total, fields) => {
      return total + (fields ? fields.length : 0);
    }, 0);
  }

  getPricingType(): 'FREE' | 'PAID' | null {
    if (!this.templatePricingData) return null;
    return (this.templatePricingData as any)?.paymentType || null;
  }

  getPricingInfo(): any {
    if (!this.templatePricingData) return null;
    return this.templatePricingData;
  }

  formatPricing(pricingConfig: any): string {
    if (!pricingConfig) return 'Not configured';

    if (typeof pricingConfig === 'string') {
      return pricingConfig;
    }

    if (typeof pricingConfig === 'object') {
      // Handle different pricing configuration formats
      if (pricingConfig.type === 'fixed' && pricingConfig.value) {
        return `${pricingConfig.value} ${pricingConfig.currency || 'RWF'}`;
      }
      if (pricingConfig.type === 'dynamic') {
        return 'Dynamic pricing configured';
      }
      if (pricingConfig.value) {
        return `${pricingConfig.value} ${pricingConfig.currency || 'RWF'}`;
      }
      // For complex objects, show a summary
      return 'Custom configuration';
    }

    return String(pricingConfig);
  }

  getWorkflowSummary(): {
    stepCount: number;
    hasConditionalLogic: boolean;
  } | null {
    if (!this.templateWorkflowData) return null;

    try {
      const workflow = this.templateWorkflowData;
      let stepCount = 0;
      let hasConditionalLogic = false;

      // Try to extract step count and logic complexity
      if (Array.isArray((workflow as any).steps)) {
        stepCount = (workflow as any).steps.length;
      } else if (
        (workflow as any).workflow &&
        Array.isArray((workflow as any).workflow.steps)
      ) {
        stepCount = (workflow as any).workflow.steps.length;
      }

      // Check for conditional logic indicators
      const workflowString = JSON.stringify(workflow).toLowerCase();
      hasConditionalLogic =
        workflowString.includes('condition') ||
        workflowString.includes('if') ||
        workflowString.includes('branch');

      return { stepCount, hasConditionalLogic };
    } catch (error) {
      return null;
    }
  }

  getTranslationFieldCount(translation: any): number {
    if (!translation) return 0;
    if (typeof translation === 'object') {
      return Object.keys(translation).length;
    }
    return 0;
  }

  getTranslationFields(translation: any): string[] {
    if (!translation) return [];
    if (typeof translation === 'object') {
      return Object.keys(translation);
    }
    return [];
  }

  // Helper method for template Object operations
  getObjectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  hasObjectKeys(obj: any): boolean {
    return obj && Object.keys(obj).length > 0;
  }

  // Safe helper to get translation fields with better null handling
  getTranslationFieldsSafe(translation: any): string[] {
    try {
      if (!translation || typeof translation !== 'object') {
        return [];
      }
      return Object.keys(translation);
    } catch (error) {
      return [];
    }
  }

  hasTranslationFields(translation: any): boolean {
    return this.getTranslationFieldsSafe(translation).length > 0;
  }
}
